import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Season } from '@shared/constants';
import { ConsoleID, getConsoleState } from 'app/config/Logger';
import { Cls, Emojis, ValueCls } from 'app/constants';
import { getSelectedTeamName, getSelectedWeek, getSelectedYear } from 'app/slices/GamesSlice';
import { getShouldFetchRanks } from 'app/slices/SyncManagerSlice';
import { UserAuthState } from 'app/slices/UserSlice';

import { NFL_WEEKS_TOTAL, TeamAbrvToTeamID } from '@shared/constants';
import { breakSeasonWeekID, breakUID, toNumber, toSeasonWeekID } from '@shared/helpers';
import { IEnhancedTeamStats, IGameStats } from '@shared/types';
import {
  addAllRankingCategories,
  getAdjustedTeamRanksForAllCategories,
  getAllSeasonWeekIDsUpToSeasonWeekID,
} from 'helpers/RankingsHelper';
import { sortWeekRankByWeekID } from 'helpers/SortHelpers';
import { createWeeklyStandings } from 'helpers/StandingsHelpers';
import { getIndexDBLoaderWebWorker, getLoadStoreRequestMessage } from 'helpers/StoreHelpers';
import { createWeeklyRankingsForAllTeams } from 'helpers/TeamHelpers';
import { updateRecordsInIDBDatabase } from 'lib/idb/IndexDbInterface';
import { memoize } from 'proxy-memoize';
import {
  AppThunk,
  GameWeek,
  IRanking,
  IRankingCategories,
  ITeamsOnByeWeek,
  ITeamStandings,
  ITeamWeekRank,
  IWorkerIDBLoaderResponseMessage,
  Maybe,
  RankingsPayload,
  RankingsPayloadByWeek,
  RootState,
  SeasonID,
  SeasonWeekID,
  Stores,
  WeekID,
} from 'types';

const CONSOLE_ON = getConsoleState(ConsoleID.RankingSlice);

// #region TYPES AND INTERFACES *********************************************************
export type RankingProps = {
  ranks: RankingsPayload[];
  rankings: RankingsPayloadByWeek;
  seasonStandings: ITeamStandings[];
  rankStoresLoadedState: RankStoresLoadedState;
};

export type RankStoresLoadedState = {
  weekRankLoaded: boolean;
  seasonRankLoaded: boolean;
  seasonStandingsLoaded: boolean;
};
// #endregion

// #region REDUX ACTIONS ****************************************************************
/**
 * Creates a week and season rank for the specified week. For week 1, it will only create the week rank.
 * For all other weeks, it will create the week rank and update the season rank. A previous week's season rank
 * is required to update the current week's season rank.
 * @param {GameWeek} weekID - The week to create the rankings for
 * @returns {AppThunk} - A redux thunk
 */
export const createWeekAndSeasonRank =
  (weekID: GameWeek): AppThunk =>
  (dispatch, getState) => {
    const teamStats: IEnhancedTeamStats[] = getState().schedule.teamResults;
    const gameStats: IGameStats[] = getState().schedule.gameResults;
    const seasonWeekID = teamStats[0].seasonWeekID;
    const weeklyRanking = createWeeklyRankingsForAllTeams(teamStats, weekID, gameStats);

    const weeklyRankPayload: RankingsPayload = {
      rankings: weeklyRanking,
      weekID: weekID,
      yearID: breakSeasonWeekID(seasonWeekID).yearID,
      seasonWeekID: seasonWeekID,
      calculated: true,
      seasonRankings: {},
    };

    const teamsOnByeWeek: Maybe<ITeamsOnByeWeek> = getState().league.byeWeekTeams.find(
      (byeWeek: ITeamsOnByeWeek) => byeWeek.weekID === weeklyRankPayload.weekID,
    );

    const adjustedWeeklyRankPayload: RankingsPayload = getAdjustedTeamRanksForAllCategories(
      weeklyRankPayload,
      teamsOnByeWeek?.teamIDs || [],
      false,
    );

    const allWeeksData = getState().stats.ranks;
    const weekRank = [...allWeeksData].sort(sortWeekRankByWeekID);

    if (weekID === GameWeek.Week1) {
      const newWeek1Rank = { ...adjustedWeeklyRankPayload, seasonRankings: adjustedWeeklyRankPayload.rankings };
      const updatedWeekRanks = [newWeek1Rank, ...weekRank.slice(1)];

      dispatch(setWeekRank(updatedWeekRanks));
      dispatch(addNFLWeeklyRanksToInboxDB(updatedWeekRanks));

      console.log(`✔️ Created WeekAndSeasonRank for week ${weekID}`);
    } else {
      const currentWeekIDIndex = toNumber(weekID) - 1;
      const previousWeekIDIndex = currentWeekIDIndex - 1;
      const rankingsForLastWeek = weekRank[previousWeekIDIndex] ?? undefined;
      const seasonRankingsForLastWeek = rankingsForLastWeek?.seasonRankings ?? undefined;
      const currentWeekRankings = adjustedWeeklyRankPayload.rankings;
      if (seasonRankingsForLastWeek && currentWeekRankings) {
        const seasonWeekRankings = addAllRankingCategories(seasonRankingsForLastWeek, currentWeekRankings, weekID);
        const fullRank: RankingsPayload = { ...adjustedWeeklyRankPayload, seasonRankings: seasonWeekRankings };
        const fullWeekAndSeasonRank = getAdjustedTeamRanksForAllCategories(fullRank, [], true);

        dispatch(updateRankByWeek(fullWeekAndSeasonRank));
        dispatch(addNFLWeeklyRanksToInboxDB([fullWeekAndSeasonRank]));

        console.log(`✔️ Created WeekAndSeasonRank for week ${weekID}`);
      } else {
        console.log(`❌ Cannot WeekAndSeasonRank for week ${weekID}`);
      }
    }
  };

/** @deprecated use `createWeekAndSeasonRank` instead */
export const createSeasonRank =
  (weekID: GameWeek): AppThunk =>
  (dispatch, getState) => {
    const allWeeksData: RankingsPayload[] = getState().stats.ranks;
    const weekRank = [...allWeeksData].sort(sortWeekRankByWeekID);

    if (weekID === GameWeek.Week1) {
      if (weekRank) {
        const week1Rank = weekRank[0];
        const newWeek1Rank = { ...week1Rank, seasonRankings: week1Rank.rankings };
        const updatedWeekRanks = [newWeek1Rank, ...weekRank.slice(1)];

        dispatch(setWeekRank(updatedWeekRanks));
        console.log(`✔️ CreatedSeasonRank | ${weekID}`);
      }
    } else {
      const currentWeekIDIndex = toNumber(weekID) - 1;
      const previousWeekIDIndex = currentWeekIDIndex - 1;
      const rankingsForLastWeek = weekRank[previousWeekIDIndex] ?? undefined;
      const seasonRankingsForLastWeek = rankingsForLastWeek?.seasonRankings ?? undefined;
      const currentWeekRankings = weekRank[currentWeekIDIndex]?.rankings ?? undefined;
      if (seasonRankingsForLastWeek && currentWeekRankings) {
        const seasonWeekRankings = addAllRankingCategories(seasonRankingsForLastWeek, currentWeekRankings, weekID);
        const fullRank: RankingsPayload = { ...weekRank[currentWeekIDIndex], seasonRankings: seasonWeekRankings };
        const completedFullRanking = getAdjustedTeamRanksForAllCategories(fullRank, [], true);
        // dispatch(updateSeasonRank(completedFullRanking));
        addNFLWeeklyRanksToInboxDB([completedFullRanking]);

        console.log(`✔️ Created season rank for week ${weekID} from createSeasonRank`);
      } else {
        console.log(`❌ Cannot create season rank for week ${weekID} from createSeasonRank`);
      }
    }
  };

export const doGenerateAdjustedWeeklyRankStats =
  (weeklyRankPayload: RankingsPayload): AppThunk =>
  (dispatch, getState) => {
    const teamsOnByeWeek: Maybe<ITeamsOnByeWeek> = getState().league.byeWeekTeams.find(
      (byeWeek: ITeamsOnByeWeek) => byeWeek.weekID === weeklyRankPayload.weekID,
    );
    const teamIDs = teamsOnByeWeek?.teamIDs || [];
    const adjustedWeeklyRankPayload: RankingsPayload = getAdjustedTeamRanksForAllCategories(
      weeklyRankPayload,
      teamIDs,
      false,
    );
    console.log(`❓ Who is calling me with ${adjustedWeeklyRankPayload.weekID}?`);
    const weekID = adjustedWeeklyRankPayload.weekID as GameWeek;
    dispatch(addNewWeekRank([adjustedWeeklyRankPayload]));
    dispatch(createSeasonRank(weekID));
  };

export const doGenerateAdjustedSeasonRankStats =
  (rankPayload: RankingsPayload): AppThunk =>
  () => {
    const adjustedWeeklyRankPayload = getAdjustedTeamRanksForAllCategories(rankPayload, [], true);
    console.log(`❓ Who is calling me with ${adjustedWeeklyRankPayload.weekID}?`);
  };

export const doGenerateSeasonStandings = (): AppThunk => () => {};
// #endregion

// #region MANUAL ADD WEEK RANKS TO IDB *************************************************

export const addNFLWeeklyRanksToInboxDB =
  (teamStats: RankingsPayload[]): AppThunk =>
  () => {
    const addedWeekRanksCb = (weekRanks: RankingsPayload[] | null) => {
      const ranksCount = weekRanks?.length || 0;
      const firstWeekInList = ranksCount > 0 ? weekRanks?.[0].weekID : 'N/A';
      const lastWeekInList = ranksCount > 0 ? weekRanks?.[ranksCount - 1].weekID : 'N/A';
      const ranksString =
        firstWeekInList === lastWeekInList ? firstWeekInList : `${firstWeekInList} - ${lastWeekInList}`;
      if (ranksCount) {
        console.log(`✔️ Added ${ranksString} Ranks into IDB`);
        // store.dispatch(addNewWeekRank(weeklyRanks));
      }
    };

    updateRecordsInIDBDatabase(Stores.WeeklyRanks, teamStats, addedWeekRanksCb);
  };
// #endregion

// #region MANUAL ADD STANDINGS TO IDB **************************************************
export const manualAddSeasonStandingsToIDB =
  (weekID: GameWeek): AppThunk =>
  (dispatch) => {
    dispatch(addSeasonStandings(weekID));
  };

export const addSeasonStandings =
  (weekID: GameWeek): AppThunk =>
  (dispatch, getState) => {
    const seasonStandings = getState().stats.seasonStandings;
    // console.log('addSeasonStandings');

    const seasonSchedule = getState().schedule.games;
    const allGameResults = getState().schedule.gameResults;
    const standings = createWeeklyStandings(weekID, seasonStandings, seasonSchedule, allGameResults);

    dispatch(addNFLSeasonStandingsToInboxDB(standings));
  };

export const addNFLSeasonStandingsToInboxDB =
  (seasonStandings: ITeamStandings[]): AppThunk =>
  (dispatch) => {
    const callback = (seasonStandings: ITeamStandings[]) => dispatch(addNewWeekStandings(seasonStandings));
    updateRecordsInIDBDatabase(Stores.SeasonStandings, seasonStandings, callback);
  };
// #endregion

// #region STATS DATA PROCESSORS FROM IDB ***********************************************
export const doProcessLoadedSeasonStandingsFromIDB =
  (seasonStandings: ITeamStandings[]): AppThunk =>
  (dispatch) => {
    dispatch(setSeasonStandings(seasonStandings));
  };

export const doProcessLoadedWeeklyRanksFromIDB =
  (weeklyRanks: RankingsPayload[]): AppThunk =>
  (dispatch) => {
    dispatch(setWeekRank(weeklyRanks));
  };

export const doProcessLoadedSeasonRanksFromIDB =
  (seasonRanks: RankingsPayload[]): AppThunk =>
  (dispatch) => {
    dispatch(setWeekRank(seasonRanks));
  };
// #endregion

// #region STATS DATA LOADERS FROM IDB **************************************************
export const updateWeekRankFromInboxDB =
  (newSeasonID: SeasonID, newWeekID: GameWeek): AppThunk =>
  (dispatch, getState) => {
    const seasonWeekID = toSeasonWeekID(newSeasonID, newWeekID);
    const hasSeasonWeekRankingData = !!getState().stats.rankings[seasonWeekID];
    const msg = `${Emojis.eyes} checking %c ${seasonWeekID} %c %c ${hasSeasonWeekRankingData} %c`;
    if (CONSOLE_ON) console.log(msg, Cls.Info, '', Cls.Loc, '');
    if (!hasSeasonWeekRankingData) {
      dispatch(setRankStoresLoadedState({ weekRankLoaded: false }));
    }
  };

export const updateRankWithDataForCurrentSeasonWeekID =
  (seasonWeekID: SeasonWeekID): AppThunk =>
  (dispatch, getState) => {
    if (seasonWeekID) {
      const ranks = getState().stats.rankings[seasonWeekID];
      if (ranks) {
        dispatch(setRankForCurrentSeasonWeekID([ranks]));
      }
    }
  };

export const updateReduxWithWeekRankData =
  (rankings: RankingsPayload): AppThunk =>
  (dispatch, getState) => {
    const currentSeasonWeekID = getState().nfl.seasonWeekSelected;
    if (rankings.seasonWeekID === currentSeasonWeekID) {
      dispatch(setWeekRank([rankings]));
    } else {
      dispatch(addNewWeekRank([rankings]));
    }
  };

export const setAllRankingsDataForCurrentSeason =
  (currentSeasonWeekID: SeasonWeekID): AppThunk =>
  (dispatch, getState) => {
    const { yearID, weekID } = breakSeasonWeekID(currentSeasonWeekID);
    const count = getState().stats.ranks.filter((r) => r.seasonWeekID.startsWith(yearID)).length;

    if (count < toNumber(weekID)) {
      const seasonWeekIDs = getAllSeasonWeekIDsUpToSeasonWeekID(currentSeasonWeekID);
      const rankings = getState().stats.rankings;

      const allWeeksData: RankingsPayload[] = seasonWeekIDs
        .map((swID) => rankings[swID])
        .filter((r): r is RankingsPayload => !!r);

      dispatch(setRankForCurrentSeasonWeekID(allWeeksData));
    }
  };

export const loadWeekRankFromInboxDB =
  (year: SeasonID, week: GameWeek): AppThunk =>
  async (dispatch, getState) => {
    if (getState().user.authState !== UserAuthState.LoggedIn) return;

    if (typeof window !== 'undefined' && window.Worker) {
      const webWorkerDbLoader: Worker = getIndexDBLoaderWebWorker(`LoadRankings-${year}`);

      const processResponse = (jsonString: string) => {
        if (CONSOLE_ON) console.log(`${Emojis.hammers} ${jsonString.length}`, Cls.Info, '', Cls.Loc, '');
        const seasonRanks: RankingsPayload[] = [JSON.parse(jsonString) as RankingsPayload];
        if (seasonRanks.length > 0) {
          dispatch(setWeekRank(seasonRanks));
        }
      };
      const shouldFetch = getShouldFetchRanks(getState());
      webWorkerDbLoader.postMessage(getLoadStoreRequestMessage(Stores.WeeklyRanks, year, shouldFetch, week));
      webWorkerDbLoader.onmessage = (e: MessageEvent<IWorkerIDBLoaderResponseMessage>) => {
        const { type, payload } = e.data;
        const msg = `${Emojis.mailNew} from ${Emojis.hammers} to %c ${Stores.WeeklyRanks} %c %c ${type} %c`;
        if (CONSOLE_ON) console.log(msg, Cls.Info, '', Cls.Loc, '');
        switch (type) {
          case 'result-get-store-data':
            processResponse(payload);
            webWorkerDbLoader.terminate();
            break;

          default:
            console.log(`${Emojis.hammers} UNKNOWN message type %c ${type} %c`, ValueCls, '');
        }
      };
    }
  };

// export const loadSeasonStandingsFromInboxDB =
//   (year: SeasonID): AppThunk =>
//   async (dispatch, getState) => {
//     if (getState().user.authState !== UserAuthState.LoggedIn) return;

//     if (typeof window !== 'undefined' && window.Worker) {
//       const webWorkerDbLoader: Worker = getIndexDBLoaderWebWorker(`LoadSeasonStandings-${year}`);

//       const processResponse = (jsonString: string) => {
//         const seasonStandings: ITeamStandings[] = JSON.parse(jsonString) as ITeamStandings[];
//         if (seasonStandings.length > 0) {
//           dispatch(doProcessLoadedSeasonStandingsFromIDB(seasonStandings));
//         } else {
//           // Start of the new season probably and we do not have any stats yet,
//           // create the first week with all zero data
//           const week1Games = getState().schedule.games;
//           const gamesForWeek = week1Games.filter((g: IGame) => g.gameWeek.replace(' ', '') === GameWeek.Week1);
//           const weeklyStandings: ITeamStandings[] = getEmptyStandingsForWeekID(gamesForWeek, GameWeek.Week1);
//           dispatch(doProcessLoadedSeasonStandingsFromIDB(weeklyStandings));
//         }
//       };
//       const shouldFetch = getShouldFetchStandings(getState());
//       webWorkerDbLoader.postMessage(getLoadStoreRequestMessage(Stores.SeasonStandings, year, shouldFetch));
//       webWorkerDbLoader.onmessage = (e: MessageEvent<IWorkerIDBLoaderResponseMessage>) => {
//         const { type, payload } = e.data;
//         const msg = `${Emojis.mailNew} from ${Emojis.hammers} to %c ${Stores.SeasonStandings} %c %c ${type} %c`;
//         if (CONSOLE_ON) console.log(msg, Cls.Info, '', Cls.Loc, '');
//         switch (type) {
//           case 'result-get-store-data':
//             processResponse(payload);
//             webWorkerDbLoader.terminate();
//             break;

//           default:
//             console.log(`${Emojis.hammers} UNKNOWN message type %c ${type} %c`, ValueCls, '');
//         }
//       };
//     }
//   };
// #endregion

// #region LOG DEBUGGER *****************************************************************
type RankingResult = {
  name: string;
  best: IRanking;
  worst: IRanking;
};
export type ResultRecords<T> = Record<string, T>;

export const debugGenerateRecordsForLog = (): AppThunk => (dispatch, getState) => {
  const curWeekNum = 15;
  const ranks = getState().stats.ranks;
  const results: Record<string, RankingResult> = {};

  for (let i = 1; i <= curWeekNum; i++) {
    const weekID = `Week${i}` as GameWeek;
    const weekRank = ranks.find((week) => week.weekID === weekID) as RankingsPayload;
    const categories = weekRank.rankings as IRankingCategories;
    const categoryKeys = Object.keys(categories) as (keyof IRankingCategories)[];

    categoryKeys.forEach((key) => {
      const teams: ITeamWeekRank[] | [] = categories[key]?.adjustedRanking ?? [];
      if (teams.length > 0) {
        const name = teams[0].rankCategory;
        const best = teams[0].bestTeam;
        const worst = teams[0].worstTeam;
        const highOrLow = teams[0].highOrLow;

        if (results[name]) {
          results[name].best =
            highOrLow === 'High'
              ? best.value > results[name].best.value
                ? best
                : results[name].best
              : best.value < results[name].best.value
                ? best
                : results[name].best;
          results[name].worst =
            highOrLow === 'High'
              ? worst.value < results[name].worst.value
                ? worst
                : results[name].worst
              : worst.value > results[name].worst.value
                ? worst
                : results[name].worst;
        } else {
          results[name] = { name, best, worst };
        }
      }
    });
  }

  const readableResults = Object.keys(results).map((key) => {
    const { name, best, worst } = results[key];
    return { name, best: `${best.teamName}: ${best.value}`, worst: `${worst.teamName}: ${worst.value}` };
  });

  console.table(readableResults);
};
// #endregion

// #region Ranking Weeks
export const initialState: RankingProps = {
  ranks: [],
  rankings: {},
  seasonStandings: [],
  rankStoresLoadedState: {
    weekRankLoaded: false,
    seasonRankLoaded: false,
    seasonStandingsLoaded: false,
  },
};
// #endregion

export const rankingSlice = createSlice({
  name: 'stats',
  initialState,
  reducers: {
    setRankForCurrentSeasonWeekID: (state, action: PayloadAction<RankingsPayload[]>) => {
      state.ranks = [...action.payload];
    },
    setWeekRank: (state, action: PayloadAction<RankingsPayload[]>) => {
      state.ranks = [...action.payload];
      state.rankings = action.payload.reduce(
        (acc, curr) => {
          acc[curr.seasonWeekID] = curr;
          return acc;
        },
        { ...state.rankings } as RankingsPayloadByWeek,
      );
      state.rankStoresLoadedState.weekRankLoaded = true;
      state.rankStoresLoadedState.seasonRankLoaded = true;
    },
    addNewWeekRank: (state, action: PayloadAction<RankingsPayload[]>) => {
      state.rankings = action.payload.reduce(
        (acc, curr) => {
          acc[curr.seasonWeekID] = curr;
          return acc;
        },
        { ...state.rankings } as RankingsPayloadByWeek,
      );
    },
    updateRankByWeek: (state, action: PayloadAction<RankingsPayload>) => {
      const { weekID, seasonWeekID } = action.payload;
      state.rankings[seasonWeekID] = { ...state.rankings[seasonWeekID], ...action.payload };
      const weekIndex = state.ranks.findIndex((week) => week.weekID === weekID);
      if (weekIndex < 0) {
        state.ranks.push(action.payload);
      } else {
        state.ranks[weekIndex] = action.payload;
      }
      state.rankStoresLoadedState.weekRankLoaded = true;
      state.rankStoresLoadedState.seasonRankLoaded = true;
    },
    setStandingsForSeason: (state, action: PayloadAction<{ seasonID: SeasonID; standings: ITeamStandings[] }>) => {
      const { seasonID, standings } = action.payload;
      state.seasonStandings = [
        ...state.seasonStandings.filter((record) => !record.seasonWeekID.startsWith(`${seasonID}-`)),
        ...standings,
      ];
      state.rankStoresLoadedState.seasonStandingsLoaded = true;
    },
    setSeasonStandings: (state, action: PayloadAction<ITeamStandings[]>) => {
      state.seasonStandings = action.payload;
      state.rankStoresLoadedState.seasonStandingsLoaded = true;
    },
    addNewWeekStandings: (state, action: PayloadAction<ITeamStandings[]>) => {
      const dedupe = Array.from(
        new Map<string, ITeamStandings>(
          [...state.seasonStandings, ...action.payload].map((item) => [item.uID, item]),
        ).values(),
      );
      state.seasonStandings = dedupe;
      // state.seasonStandings.push(...action.payload);
      state.rankStoresLoadedState.seasonStandingsLoaded = true;
    },
    setRankStoresLoadedState: (state, action: PayloadAction<Partial<RankStoresLoadedState>>) => {
      state.rankStoresLoadedState = { ...state.rankStoresLoadedState, ...action.payload };
    },
    resetWeeklyRankStats: () => {
      return { ...initialState };
    },
  },
});

export const {
  setRankForCurrentSeasonWeekID,
  setWeekRank,
  addNewWeekRank,
  updateRankByWeek,
  setSeasonStandings,
  setStandingsForSeason,
  addNewWeekStandings,
  setRankStoresLoadedState,
  resetWeeklyRankStats,
} = rankingSlice.actions;

// #region Selectors ************************************************************
export type TeamNameKey = keyof typeof TeamAbrvToTeamID;

export const getRanksForAllWeeks = (state: RootState): RankingsPayload[] => state.stats.ranks;
export const getRankingsByWeek = (state: RootState): RankingsPayloadByWeek => state.stats.rankings;
export const getSeasonStandings = (state: RootState): ITeamStandings[] => state.stats.seasonStandings;

export const selectRanksByYearAndWeek = (state: RootState, yearID: SeasonID, weekID: WeekID): RankingsPayload[] => {
  const ranks = getRankingsByWeek(state);
  return [ranks[toSeasonWeekID(yearID, weekID)]];
};
export const selectRankingsBySeasonWeekID = (
  state: RootState,
  seasonWeekID: SeasonWeekID,
): RankingsPayload | undefined => state.stats.rankings[seasonWeekID];

export const getLoadedSeasonWeekIDsBySeasonID = (state: RootState, seasonID: SeasonID): SeasonWeekID[] => {
  return Object.keys(state.stats.rankings)
    .sort()
    .filter((seasonWeekID) => seasonWeekID.startsWith(seasonID)) as SeasonWeekID[];
};

export const getLoadedSeasonWeekIDsForCurrentSeason = (state: RootState): SeasonWeekID[] => {
  const selectedYear = getSelectedYear(state);
  return getLoadedSeasonWeekIDsBySeasonID(state, selectedYear);
};

export const getLoadedSeasonWeekIDsForAllSeasons = memoize((state: RootState): SeasonWeekID[] => {
  const availableSeasons = Object.values(Season) as SeasonID[];
  return availableSeasons.flatMap((seasonID) => getLoadedSeasonWeekIDsBySeasonID(state, seasonID));
});

export const getRankStoresLoadedState = (state: RootState): RankStoresLoadedState => state.stats.rankStoresLoadedState;
export const getIsRankingLoaded = (state: RootState): boolean => state.stats.rankStoresLoadedState.weekRankLoaded;
export const getIsSeasonRankLoaded = (state: RootState): boolean => state.stats.rankStoresLoadedState.seasonRankLoaded;
export const getIsStandingsLoaded = (state: RootState): boolean =>
  state.stats.rankStoresLoadedState.seasonStandingsLoaded;

export const getSelectedTeamStanding = memoize((state: RootState): ITeamStandings[] | [] => {
  const standings = getSeasonStandings(state);
  const teamName = getSelectedTeamName(state);
  if (!standings || !teamName) return [];
  return standings.filter((record) => breakUID(record.uID).teamAbrv === teamName);
});

export const getSelectedTeamAllRanks = memoize((state: RootState): IRankingCategories => {
  const selectedTeam = getSelectedTeamName(state);
  const selectedWeek = getSelectedWeek(state);
  const selectedYear = getSelectedYear(state);
  const ranks: RankingsPayloadByWeek = getRankingsByWeek(state);
  const keys = Object.keys(ranks).filter((key) => key.startsWith(selectedYear));
  const weekRanks = keys.map((k) => ranks[k as SeasonWeekID]);

  if (!selectedTeam || !selectedWeek || !weekRanks) {
    // const msg = `${Emojis.eyes} selectedTeam: %c ${selectedTeam} %c, selectedWeek: %c ${selectedWeek} %c, weekRanks: %c ${weekRanks} %c`;
    // console.log(msg, ValueCls, '', ValueCls, '', ValueCls, '');
    return {};
  }

  const teamID = TeamAbrvToTeamID[selectedTeam as TeamNameKey];
  const curWeekNum = toNumber(selectedWeek);
  const oneTeamWeeklyRanks: IRankingCategories = {};

  for (let i = 1; i <= curWeekNum; i++) {
    const weekKey = `Week${i}` as GameWeek;
    const weekRank = weekRanks.find(
      (week) => week.weekID === weekKey && week.seasonWeekID?.startsWith(selectedYear),
    ) as RankingsPayload;
    if (weekRank) {
      const categories = weekRank.rankings as IRankingCategories;
      const categoryKeys = Object.keys(categories) as (keyof IRankingCategories)[];

      categoryKeys.forEach((key) => {
        const team = categories[key]?.adjustedRanking?.find((team) => team.teamID === teamID);
        if (weekRank.rankings[key] && team) {
          if (oneTeamWeeklyRanks[key] && oneTeamWeeklyRanks[key].adjustedRanking) {
            oneTeamWeeklyRanks[key].adjustedRanking.push(team);
          } else {
            oneTeamWeeklyRanks[key] = { ...weekRank.rankings[key] };
            oneTeamWeeklyRanks[key].adjustedRanking = [team];
          }
        }
      });
    }
  }

  return oneTeamWeeklyRanks;
});

export const getSelectedTeamSelectedSeasonRanks = memoize((state: RootState): IRankingCategories => {
  const selectedTeam = getSelectedTeamName(state);
  const selectedYear = getSelectedYear(state);
  const ranks: RankingsPayloadByWeek = getRankingsByWeek(state);
  const keys = Object.keys(ranks).filter((key) => key.startsWith(selectedYear));
  const weekRanks = keys.map((k) => ranks[k as SeasonWeekID]);

  if (!selectedTeam || !weekRanks) {
    // const msg = `${Emojis.eyes} selectedTeam: %c ${selectedTeam} %c, selectedWeek: %c ${selectedWeek} %c, weekRanks: %c ${weekRanks} %c`;
    // console.log(msg, ValueCls, '', ValueCls, '', ValueCls, '');
    return {};
  }

  const teamID = TeamAbrvToTeamID[selectedTeam as TeamNameKey];
  const oneTeamWeeklyRanks: IRankingCategories = {};

  for (let i = 1; i <= NFL_WEEKS_TOTAL; i++) {
    const weekKey = `Week${i}` as GameWeek;
    const weekRank = weekRanks.find(
      (week) => week.weekID === weekKey && week.seasonWeekID?.startsWith(selectedYear),
    ) as RankingsPayload;
    if (weekRank) {
      const categories = weekRank.rankings as IRankingCategories;
      const categoryKeys = Object.keys(categories) as (keyof IRankingCategories)[];

      categoryKeys.forEach((key) => {
        const team = categories[key]?.adjustedRanking?.find((team) => team.teamID === teamID);
        if (weekRank.rankings[key] && team) {
          if (oneTeamWeeklyRanks[key] && oneTeamWeeklyRanks[key].adjustedRanking) {
            oneTeamWeeklyRanks[key].adjustedRanking.push(team);
          } else {
            oneTeamWeeklyRanks[key] = { ...weekRank.rankings[key] };
            oneTeamWeeklyRanks[key].adjustedRanking = [team];
          }
        }
      });
    }
  }

  return oneTeamWeeklyRanks;
});
// #endregion

export default rankingSlice.reducer;
